import { z } from 'zod/v4';
// Priority levels for SLA calculations
export const SlaPrioritySchema = z.enum([
    'standard', // Within 3 business days
    'priority', // Next business day
    'urgent', // Same day (end of business)
    'on-demand', // Specific time requested by customer
]);
// SLA target definitions
// These represent the deadline commitment, not hours of work
export const SlaTargetsConfig = {
    standard: { days: 3, description: 'Within 3 business days' },
    priority: { days: 1, description: 'Next business day' },
    urgent: { days: 0, description: 'Same day (end of business)' },
    'on-demand': { days: null, description: 'Customer-specified deadline' },
};
// Schema for SLA target configuration
export const SlaTargetSchema = z.object({
    priority: SlaPrioritySchema,
    targetBusinessHours: z.number().nullable(),
    description: z.string(),
});
// SLA snapshot period types
export const SlaPeriodSchema = z.enum(['daily', 'weekly', 'monthly']);
// SLA snapshot for tracking operator performance over time
export const SlaSnapshotSchema = z.object({
    id: z.string().uuid(),
    operatorId: z.string().uuid(),
    period: SlaPeriodSchema,
    periodStart: z.string().datetime(),
    periodEnd: z.string().datetime(),
    // Throughput metrics
    itemsReceived: z.number().default(0),
    itemsCompleted: z.number().default(0),
    itemsInProgress: z.number().default(0),
    // Time-based metrics (in seconds)
    avgTimeToFirstResponse: z.number().optional(),
    avgTimeToResolution: z.number().optional(),
    minTimeToResolution: z.number().optional(),
    maxTimeToResolution: z.number().optional(),
    // Compliance metrics (0-100%)
    dueDateComplianceRate: z.number().min(0).max(100),
    firstResponseComplianceRate: z.number().min(0).max(100).optional(),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateSlaSnapshotSchema = SlaSnapshotSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateSlaSnapshotSchema = SlaSnapshotSchema.partial().omit({
    id: true,
    operatorId: true,
    period: true,
    periodStart: true,
    createdAt: true,
});
// Aggregated metrics for dashboard display
export const SlaMetricsOverviewSchema = z.object({
    period: SlaPeriodSchema,
    periodStart: z.string().datetime(),
    periodEnd: z.string().datetime(),
    // Team-wide metrics
    totalItemsReceived: z.number(),
    totalItemsCompleted: z.number(),
    totalItemsInProgress: z.number(),
    totalItemsUnassigned: z.number(),
    // Average performance
    avgTimeToFirstResponse: z.number().optional(),
    avgTimeToResolution: z.number().optional(),
    dueDateComplianceRate: z.number(),
    // SLA breach counts
    breachedItems: z.number(),
    atRiskItems: z.number(),
});
